import keyBy from 'lodash/keyBy';
import mapValues from 'lodash/mapValues';
import uniqBy from 'lodash/uniqBy';
import { all, call, fork, put, takeEvery, takeLatest } from 'redux-saga/effects';
import * as API from '../../api';
import { ApiResponse, IApiQueryOptions } from '../../api/ApiClient';
import { EmployeeType, IAction, IApiList, IEmployer, IEmployerResponse, IEmployerSite, ISite } from '../../types';
import { IEmployerState } from '../types';
import * as Actions from './actions';
import { getEmployerSitesError, getEmployerSitesSuccess } from './actions';
import { Employers } from './types';

export function* getEmployers() {
  try {
    const response: ApiResponse<IEmployerResponse> = yield call(API.Employers.listEmployers);

    const keyedEmployers = keyBy(response.body.employers, 'id');
    const employersWithMappedSites: Record<string, IEmployerState> = mapValues(keyedEmployers, (employer: IEmployer) => ({
      ...employer,
      sites: keyBy(employer.sites, 'id'),
    }));

    yield put(Actions.getEmployersSuccess(employersWithMappedSites));
  } catch (error: any) {
    yield put(Actions.getEmployersError(error));
  }
}

interface IEmployersPayload {
  onSuccess: (response: Array<IEmployerSite | IEmployer>) => void;
}

export function* getUserDashboardEmployers({ payload }: IAction<Employers, IEmployersPayload>) {
  try {
    const response: ApiResponse<IEmployerResponse> = yield call(API.Employers.listEmployers, { includeActionRequired: true, includeDismissed: true });
    const employers: Array<IEmployerSite | IEmployer> = [];
    response.body.employers?.forEach((employer) => {
      if (employer.type === EmployeeType.ADMIN) return employers.push(employer);

      Object.values(employer.sites).forEach((site) => {
        employers.push(site);
      });

      return null;
    });

    const normalizedEmployers = uniqBy(employers, 'id');
    yield call(payload.onSuccess, normalizedEmployers);
    // The response is passed to the success function below for better debugging, but it is not being stored on redux
    yield put(Actions.getUserDashboardEmployersSuccess(normalizedEmployers));
  } catch (error: any) {
    yield put(Actions.getUserDashboardEmployersError(error));
  }
}

interface IEmployerSitesPayload {
  orgId: string;
  onSuccess: (response: IApiList<ISite>) => void;
  options?: IApiQueryOptions;
}

export function* getEmployerSites({ payload }: IAction<Employers, IEmployerSitesPayload>) {
  try {
    const response: ApiResponse<IApiList<ISite>> = yield call(API.Employers.listEmployerSites, payload.orgId, payload.options);

    yield call(payload.onSuccess, response.body);
    // The response is passed to the success function below for better debugging, but it is not being stored on redux
    yield put(getEmployerSitesSuccess(response.body));
  } catch (error: any) {
    yield put(getEmployerSitesError(error));
  }
}

export function* watchGetEmployers() {
  yield takeLatest(Employers.GET_EMPLOYERS_REQUEST, getEmployers);
}

export function* watchGetUserDashboardEmployers() {
  yield takeEvery(Employers.GET_USER_DASHBOARD_EMPLOYERS_REQUEST, getUserDashboardEmployers);
}

export function* watchGetEmployerSites() {
  yield takeEvery(Employers.GET_EMPLOYER_SITES_REQUEST, getEmployerSites);
}

function* employersSaga() {
  yield all([fork(watchGetEmployers), fork(watchGetUserDashboardEmployers), fork(watchGetEmployerSites)]);
}
export { employersSaga };
